import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import AttractionsList from "@/components/attractions/AttractionsList";
import { useToast } from "@/hooks/use-toast";
import { deleteAllAttractions } from "@/lib/attractionsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Attractions = () => {
  const { user, userRole, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      await deleteAllAttractions();
      toast({
        title: "All attractions deleted",
        description: "All data has been removed from this screen.",
      });
      setDeleteAllDialogOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (error: any) {
      toast({
        title: "Error deleting attractions",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !userRole) {
    return null;
  }

  const canImport = userRole === "ceo" || userRole === "general_manager";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-end gap-2">
          {canImport && (
            <Button
              variant="outline"
              onClick={() => navigate("/import-attractions")}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Attractions
            </Button>
          )}
        </div>
        <AttractionsList key={refreshKey} />

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all attractions</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all attractions and their tickets from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Attractions;
